import React, { useState, useEffect } from "react";
import {
  useDataProvider,
  Create,
  SimpleForm,
  TextInput,
  NumberInput,
  ArrayInput,
  SimpleFormIterator,
  SelectInput,
  ReferenceArrayInput,
  SelectArrayInput,
  useNotify,
  ImageInput,
  useGetList,
  AutocompleteArrayInput,
  BooleanInput,
} from "react-admin";
import { useNavigate } from "react-router-dom";
import { PURCHASE_SPLIT } from "../enums/PurchaseSplitOptions";
import { STOCK_TYPES } from "../enums/StockTypes";
import {
  DELIVERY_METHODS_OPTIONS,
  DISCOUNT_MAPPER,
  DISCOUNT_OPTIONS,
  filterTimes,
  getDistinctDaysFomObject,
  PRODUCT_TYPES_OPTIONS,
  PURCHASE_SPLIT_OPTIONS,
} from "../utils/ProductUtils";
import { DELIVERY_METHODS } from "../enums/DeliveryMethods";
import { PRODUCT_TYPES } from "../enums/ProductTypes";
import {
  validatePrice,
  validateProductDescription,
  validateProductName,
  validateRequired,
} from "../utils/Validations";
import ImagePreview from "../common/ImagePreview";
import { IMAGE_MAX_SIZE } from "../constants/FileConstants";
import { formatDateToLocalISOString } from "../utils/DateTimeUtils";
import { getPremiumPlanId } from "../utils/SubscriptionUtils";
import { RESTAURANT_TYPES } from "../enums/RestaurantTypes";
import { GenericModal } from "../common/Modal";
import { ENTITY_AVAILABILITY_STATUS_OPTIONS } from "../utils/EntityAvailabilityStatusUtils";
import { ENTITY_AVAILABILITY_STATUS } from "../enums/EntityAvailabilityStatus";
import { usePrompt } from "../hooks/usePrompt";

const CUSTOM_PURCHASE_SPLIT_OPTIONS = PURCHASE_SPLIT_OPTIONS.filter(
  (e) => e.id !== PURCHASE_SPLIT.CUSTOM_PERCENTAGE
);

const ProductBulkCreate = () => {
  const navigate = useNavigate();
  const notify = useNotify();
  const dataProvider = useDataProvider();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  const [modalOpen, setModalOpen] = useState(false);
  const [modalData, setModalData] = useState({
    title: "",
    message: "",
    errors: [],
  });

  usePrompt(
    "¿Estás seguro de que quieres salir? Los cambios no guardados se perderán.",
    hasUnsavedChanges
  );

  const { data: subscriptionPlans } = useGetList("subscription-plans", {
    pagination: { page: 1, perPage: 5 },
    sort: { field: "name", order: "ASC" },
  });

  const premiumPlanId = getPremiumPlanId(subscriptionPlans);

  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        e.returnValue =
          "¿Estás seguro de que quieres salir? Los cambios no guardados se perderán.";
        return e.returnValue;
      }
    };

    const handlePopState = (e) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        e.returnValue = "";
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
      window.removeEventListener("popstate", handlePopState);
    };
  }, [hasUnsavedChanges]);

  const handleFormChange = () => {
    if (!hasUnsavedChanges) {
      setHasUnsavedChanges(true);
    }
  };

  const handleSubmit = async (data) => {
    try {
      setIsSubmitting(true);
      setHasUnsavedChanges(false);
      const { restaurants, products } = data;
      const results = await Promise.allSettled(
        restaurants.map(async (restaurant) => {
          const { data: restaurantData } = await dataProvider.getOne(
            "restaurants",
            {
              id: restaurant,
              active: true,
              type: RESTAURANT_TYPES.REGULAR,
            }
          );

          const results = await Promise.allSettled(
            products.map(async (product) => {
              try {
                const discounts =
                  DISCOUNT_MAPPER[product.discount](premiumPlanId);
                const daysRestaurantIsOpen = getDistinctDaysFomObject(
                  restaurantData.timeTable
                );
                const formmatedDiscounts = filterTimes(
                  discounts,
                  daysRestaurantIsOpen
                );

                const productData = {
                  ...product,
                  restaurant: restaurant,
                  discounts: formmatedDiscounts,
                  stockType: STOCK_TYPES.UNLIMITED,
                };
                if (productData.type === PRODUCT_TYPES.PURCHASE) {
                  const daysThatHaveDiscounts =
                    getDistinctDaysFomObject(discounts);

                  productData.offerDeliveryAvailability = filterTimes(
                    restaurantData.timeTable,
                    daysThatHaveDiscounts
                  ).map(({ days, times }) => ({
                    days,
                    times: times.map(({ opening, closing }) => ({
                      opening: formatDateToLocalISOString(
                        opening,
                        restaurant.country
                      ),
                      closing: formatDateToLocalISOString(
                        closing,
                        restaurant.country
                      ),
                    })),
                  }));
                }
                const response = await dataProvider.create("products", {
                  data: productData,
                });
                return {
                  success: true,
                  data: response?.data,
                  restaurant: restaurantData?.name,
                };
              } catch (error) {
                return {
                  success: false,
                  product: product.name,
                  error: error.response?.data || error.message,
                  restaurant: restaurantData?.name,
                };
              }
            })
          );
          const success = results.every(
            (result) => result.value?.success === true
          );
          const errors = results
            .filter((result) => result.value?.success === false)
            .map((e) => ({
              product: e.value.product,
              restaurant: e.value.restaurant,
            }));
          return { success, errors };
        })
      );

      if (results.every((result) => result.value?.success === true)) {
        notify("Productos creados correctamente", { type: "success" });
        return navigate("/products");
      }

      const values = results
        .filter((result) => result.value?.success === false)
        .map((result) => result.value);

      const errors = values
        .map((e) =>
          e.errors.map((err) => ({
            key: `Restaurant: ${err.restaurant}`,
            value: `Producto: ${err.product}`,
          }))
        )
        .flat();

      setModalData({
        title: "Error al crear productos",
        message: "Los siguientes productos no se han creado correctamente:",
        errors: errors,
      });
      setModalOpen(true);
    } catch (error) {
      notify("Error al crear productos", { type: "error" });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Prepare choices for discount select input
  const discountChoices = Object.values(DISCOUNT_OPTIONS).map((option) => ({
    id: option.id,
    name: option.name,
  }));

  return (
    <>
      <GenericModal
        open={modalOpen}
        onClose={() => {
          setModalOpen(false);
          setHasUnsavedChanges(false);
          navigate("/products");
        }}
        data={modalData}
      />
      <Create title="Crear múltiples">
        <SimpleForm
          onSubmit={handleSubmit}
          initialValues={{ products: [{}] }}
          disabled={isSubmitting}
          onChange={handleFormChange}
        >
          <ReferenceArrayInput
            source="restaurants"
            reference="restaurants"
            perPage={100}
            filter={{
              activeOrOnboarding: true,
              type: RESTAURANT_TYPES.REGULAR,
              toteat: false,
            }}
            sort={{ field: "name", order: "ASC" }}
          >
            <AutocompleteArrayInput
              filterToQuery={(searchText) => ({
                name: searchText,
              })}
              label="Restaurante(s)"
              helperText="Puedes elegir más de un restaurante"
              fullWidth
              optionText="name"
            />
          </ReferenceArrayInput>

          <ArrayInput source="products" label="Productos">
            <SimpleFormIterator disableReordering={true}>
              <TextInput
                source="name"
                label="Nombre"
                validate={validateProductName}
                fullWidth
              />
              <TextInput
                source="description"
                label="Descripción"
                multiline
                validate={validateProductDescription}
                fullWidth
                defaultValue={
                  "\n\n*No acumulable con otras promociones del local."
                }
              />
              <NumberInput
                source="price"
                label="Precio"
                validate={validatePrice}
                min={1}
                fullWidth
                onWheel={(e) => e.target.blur()}
              />
              <SelectInput
                source="discount"
                label="Descuento"
                choices={discountChoices}
                validate={validateRequired}
                fullWidth
                defaultValue={DISCOUNT_OPTIONS.REGULAR_PROMO_25_20.id}
              />
              <ReferenceArrayInput
                source="categories"
                reference="categories"
                fullWidth
                perPage={100}
              >
                <SelectArrayInput
                  optionText="name"
                  fullWidth
                  validate={validateRequired}
                  label="Categorías"
                  helperText="Puedes elegir más de una categoría"
                />
              </ReferenceArrayInput>
              <SelectInput
                source="type"
                label="Tipo"
                choices={PRODUCT_TYPES_OPTIONS}
                fullWidth
                validate={validateRequired}
              />
              <SelectInput
                source="purchaseSplitOption"
                label="Comisión"
                choices={CUSTOM_PURCHASE_SPLIT_OPTIONS}
                fullWidth
                defaultValue={PURCHASE_SPLIT.RESTAURANT_PERCENTAGE}
                validate={validateRequired}
              />
              <SelectArrayInput
                source="deliveryMethods"
                label="Métodos de entrega"
                choices={DELIVERY_METHODS_OPTIONS}
                fullWidth
                defaultValue={[
                  DELIVERY_METHODS.TAKE_AWAY,
                  DELIVERY_METHODS.ON_SITE,
                ]}
                validate={validateRequired}
              />

              <SelectInput
                label="Estado"
                source="status"
                choices={ENTITY_AVAILABILITY_STATUS_OPTIONS.filter(
                  (status) => status.id !== ENTITY_AVAILABILITY_STATUS.ARCHIVED
                )}
                validate={validateRequired}
                defaultValue={ENTITY_AVAILABILITY_STATUS.ACTIVE}
                fullWidth
              />

              <BooleanInput
                source="deactivationLocked"
                label="Bloquear desactivación"
                helperText="Si está activo, el local no podrá desactivar este producto"
              />

              <ImageInput
                source="images"
                accept={{
                  "image/*": [],
                }}
                label="Imágenes"
                multiple
                maxSize={IMAGE_MAX_SIZE}
                fullWidth
              >
                <ImagePreview source="url" />
              </ImageInput>
            </SimpleFormIterator>
          </ArrayInput>
        </SimpleForm>
      </Create>
    </>
  );
};

export default ProductBulkCreate;
